// 4D Grid - Multiple interconnected pits
// Dimensions: X (width), Y (height/depth), Z (depth), W (parallel dimensions/pits)

export interface Cell {
  filled: boolean;
  color: number;
}

export interface GridConfig {
  sizeX: number; // Width of each pit
  sizeY: number; // Height of each pit (blocks fall along Y)
  sizeZ: number; // Depth of each pit
  sizeW: number; // Number of parallel pits (4th dimension)
}

export class Grid4D {
  private grid: Cell[][][][]; // [w][y][z][x]
  public readonly config: GridConfig;

  constructor(config: GridConfig) {
    this.config = config;
    this.grid = this.createEmptyGrid();
  }

  private createEmptyGrid(): Cell[][][][] {
    const grid: Cell[][][][] = [];

    for (let w = 0; w < this.config.sizeW; w++) {
      grid[w] = [];
      for (let y = 0; y < this.config.sizeY; y++) {
        grid[w][y] = [];
        for (let z = 0; z < this.config.sizeZ; z++) {
          grid[w][y][z] = [];
          for (let x = 0; x < this.config.sizeX; x++) {
            grid[w][y][z][x] = { filled: false, color: 0 };
          }
        }
      }
    }

    return grid;
  }

  // Check if a position is within bounds
  isInBounds(x: number, y: number, z: number, w: number): boolean {
    return (
      x >= 0 &&
      x < this.config.sizeX &&
      y >= 0 &&
      y < this.config.sizeY &&
      z >= 0 &&
      z < this.config.sizeZ &&
      w >= 0 &&
      w < this.config.sizeW
    );
  }

  // Check if a cell is empty
  isEmpty(x: number, y: number, z: number, w: number): boolean {
    if (!this.isInBounds(x, y, z, w)) {
      return false;
    }
    return !this.grid[w][y][z][x].filled;
  }

  // Get cell at position
  getCell(x: number, y: number, z: number, w: number): Cell | null {
    if (!this.isInBounds(x, y, z, w)) {
      return null;
    }
    return this.grid[w][y][z][x];
  }

  // Set cell at position
  setCell(x: number, y: number, z: number, w: number, filled: boolean, color: number): void {
    if (this.isInBounds(x, y, z, w)) {
      this.grid[w][y][z][x] = { filled, color };
    }
  }

  // Get all filled cells (for rendering)
  getFilledCells(): { x: number; y: number; z: number; w: number; color: number }[] {
    const cells: { x: number; y: number; z: number; w: number; color: number }[] = [];

    for (let w = 0; w < this.config.sizeW; w++) {
      for (let y = 0; y < this.config.sizeY; y++) {
        for (let z = 0; z < this.config.sizeZ; z++) {
          for (let x = 0; x < this.config.sizeX; x++) {
            const cell = this.grid[w][y][z][x];
            if (cell.filled) {
              cells.push({ x, y, z, w, color: cell.color });
            }
          }
        }
      }
    }

    return cells;
  }

  // Get filled cells for a specific W slice
  getFilledCellsAtW(targetW: number): { x: number; y: number; z: number; color: number }[] {
    const cells: { x: number; y: number; z: number; color: number }[] = [];

    if (targetW < 0 || targetW >= this.config.sizeW) {
      return cells;
    }

    for (let y = 0; y < this.config.sizeY; y++) {
      for (let z = 0; z < this.config.sizeZ; z++) {
        for (let x = 0; x < this.config.sizeX; x++) {
          const cell = this.grid[targetW][y][z][x];
          if (cell.filled) {
            cells.push({ x, y, z, color: cell.color });
          }
        }
      }
    }

    return cells;
  }

  // Check and clear complete layers
  // A layer is complete when all cells in a Y plane are filled across ALL W dimensions
  // This is the 4D twist - you need to fill layers in all parallel dimensions!
  clearCompleteLines(): number {
    let linesCleared = 0;

    // Check each Y level
    for (let y = this.config.sizeY - 1; y >= 0; y--) {
      if (this.isLayerComplete(y)) {
        this.clearLayer(y);
        this.dropLayersAbove(y);
        linesCleared++;
        y++; // Check the same level again since everything dropped
      }
    }

    return linesCleared;
  }

  // Check if a Y layer is complete across all W dimensions
  private isLayerComplete(y: number): boolean {
    for (let w = 0; w < this.config.sizeW; w++) {
      for (let z = 0; z < this.config.sizeZ; z++) {
        for (let x = 0; x < this.config.sizeX; x++) {
          if (!this.grid[w][y][z][x].filled) {
            return false;
          }
        }
      }
    }
    return true;
  }

  // Clear a layer across all W dimensions
  private clearLayer(y: number): void {
    for (let w = 0; w < this.config.sizeW; w++) {
      for (let z = 0; z < this.config.sizeZ; z++) {
        for (let x = 0; x < this.config.sizeX; x++) {
          this.grid[w][y][z][x] = { filled: false, color: 0 };
        }
      }
    }
  }

  // Drop all layers above the cleared layer
  private dropLayersAbove(clearedY: number): void {
    for (let y = clearedY; y > 0; y--) {
      for (let w = 0; w < this.config.sizeW; w++) {
        for (let z = 0; z < this.config.sizeZ; z++) {
          for (let x = 0; x < this.config.sizeX; x++) {
            this.grid[w][y][z][x] = { ...this.grid[w][y - 1][z][x] };
          }
        }
      }
    }

    // Clear top layer
    for (let w = 0; w < this.config.sizeW; w++) {
      for (let z = 0; z < this.config.sizeZ; z++) {
        for (let x = 0; x < this.config.sizeX; x++) {
          this.grid[w][0][z][x] = { filled: false, color: 0 };
        }
      }
    }
  }

  // Alternative: Clear lines that are complete in a single W dimension
  // This is an easier mode where you don't need to coordinate across all dimensions
  clearCompleteLinesPerDimension(): number {
    let totalCleared = 0;

    for (let w = 0; w < this.config.sizeW; w++) {
      for (let y = this.config.sizeY - 1; y >= 0; y--) {
        if (this.isLayerCompleteInW(y, w)) {
          this.clearLayerInW(y, w);
          this.dropLayersAboveInW(y, w);
          totalCleared++;
          y++;
        }
      }
    }

    return totalCleared;
  }

  private isLayerCompleteInW(y: number, w: number): boolean {
    for (let z = 0; z < this.config.sizeZ; z++) {
      for (let x = 0; x < this.config.sizeX; x++) {
        if (!this.grid[w][y][z][x].filled) {
          return false;
        }
      }
    }
    return true;
  }

  private clearLayerInW(y: number, w: number): void {
    for (let z = 0; z < this.config.sizeZ; z++) {
      for (let x = 0; x < this.config.sizeX; x++) {
        this.grid[w][y][z][x] = { filled: false, color: 0 };
      }
    }
  }

  private dropLayersAboveInW(clearedY: number, w: number): void {
    for (let y = clearedY; y > 0; y--) {
      for (let z = 0; z < this.config.sizeZ; z++) {
        for (let x = 0; x < this.config.sizeX; x++) {
          this.grid[w][y][z][x] = { ...this.grid[w][y - 1][z][x] };
        }
      }
    }

    for (let z = 0; z < this.config.sizeZ; z++) {
      for (let x = 0; x < this.config.sizeX; x++) {
        this.grid[w][0][z][x] = { filled: false, color: 0 };
      }
    }
  }

  // Reset the grid
  reset(): void {
    this.grid = this.createEmptyGrid();
  }

  // Get fill percentage for a W dimension (for visualization)
  getFillPercentage(w: number): number {
    let filled = 0;
    let total = this.config.sizeX * this.config.sizeY * this.config.sizeZ;

    for (let y = 0; y < this.config.sizeY; y++) {
      for (let z = 0; z < this.config.sizeZ; z++) {
        for (let x = 0; x < this.config.sizeX; x++) {
          if (this.grid[w][y][z][x].filled) {
            filled++;
          }
        }
      }
    }

    return filled / total;
  }

  // Serialize grid state for saving
  serialize(): string {
    const data = {
      config: this.config,
      cells: this.getFilledCells(),
    };
    return JSON.stringify(data);
  }

  // Deserialize and restore grid state
  static deserialize(json: string): Grid4D {
    const data = JSON.parse(json);
    const grid = new Grid4D(data.config);

    for (const cell of data.cells) {
      grid.setCell(cell.x, cell.y, cell.z, cell.w, true, cell.color);
    }

    return grid;
  }

  // Load state into existing grid
  loadState(json: string): void {
    const data = JSON.parse(json);

    // Verify config matches
    if (
      data.config.sizeX !== this.config.sizeX ||
      data.config.sizeY !== this.config.sizeY ||
      data.config.sizeZ !== this.config.sizeZ ||
      data.config.sizeW !== this.config.sizeW
    ) {
      throw new Error('Grid config mismatch');
    }

    this.reset();
    for (const cell of data.cells) {
      this.setCell(cell.x, cell.y, cell.z, cell.w, true, cell.color);
    }
  }
}
